'use client';

import React, { useEffect, useState } from 'react';
import { Book, Plus, ChevronDown, ChevronRight, GripVertical } from 'lucide-react';
import Link from 'next/link';
import { useUser } from '@clerk/nextjs';

const ADMIN_ID = "user_3AtnbF82V4ROWYZ4rH2PlrSsvGk";

export default function CodeHub() {
    const { user } = useUser();
    const [policies, setPolicies] = useState<any[]>([]);
    const [chapters, setChapters] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [newChapterName, setNewChapterName] = useState('');
    const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

    const [isAdmin, setIsAdmin] = useState(false);

    const isEligibleAdmin = user?.id === ADMIN_ID;

   const fetchData = async () => {
        try {
            const [billsRes, chaptersRes] = await Promise.all([
                fetch('/api/bills/effective/list/directions', { cache: 'no-store' }),
                fetch('/api/bills/chapters/directions', { cache: 'no-store' })
            ]);

            const billsData = await billsRes.json();
            const chaptersData = await chaptersRes.json();

            if (Array.isArray(billsData)) {
                const sorted = [...billsData].sort((a, b) => {
                    if (a.position != null && b.position != null) {
                        return a.position - b.position;
                    }
                    if (a.position != null) return -1;
                    if (b.position != null) return 1;
                    
                    return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
                });
                setPolicies(sorted);
            }
            if (Array.isArray(chaptersData)) setChapters(chaptersData);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const addChapter = async () => {
        if (!newChapterName) return;
        try {
            const response = await fetch('/api/bills/create-chapter/directions', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: newChapterName })
            });
            if (response.ok) {
                setNewChapterName('');
                fetchData();
            }
        } catch (error) {
            console.error('Failed to create:', error);
        }
    };

    const toggleCollapse = (chapterId: string) => {
        setCollapsed(prev => ({ ...prev, [chapterId]: !prev[chapterId] }));
    };

    const handleDragStart = (e: React.DragEvent, billId: string) => {
        if (!isAdmin) return;
        e.dataTransfer.setData("billId", billId);
    };

    const handleDragOver = (e: React.DragEvent) => {
        if (!isAdmin) return;
        e.preventDefault();
    };

    const handleDropOnChapter = async (e: React.DragEvent, targetTagName: string) => {
        if (!isAdmin) return;
        e.preventDefault();
        e.stopPropagation();
        const billId = e.dataTransfer.getData("billId");

        const updatedPolicies = policies.map(p =>
            p.id === billId ? { ...p, tags: [targetTagName] } : p
        );

        const updates = updatedPolicies.map((p, i) => ({
            id: p.id,
            position: i,
            tags: p.tags,
            title: p.title,
            category: p.category
        }));

        try {
            const response = await fetch('/api/bills/update-position', {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ updates })
            });
            if (response.ok) fetchData();
        } catch (error) {
            console.error('Chapter update failed:', error);
        }
    };

    const handleDropOnArticle = async (e: React.DragEvent, targetPolicy: any) => {
        if (!isAdmin) return;
        e.preventDefault();
        e.stopPropagation();

        const draggedId = e.dataTransfer.getData('billId');
        if (draggedId === targetPolicy.id) return;

        const reordered = [...policies];
        const draggedIdx = reordered.findIndex(p => p.id === draggedId);
        const targetIdx = reordered.findIndex(p => p.id === targetPolicy.id);

        const [movedItem] = reordered.splice(draggedIdx, 1);
        movedItem.tags = targetPolicy.tags;
        reordered.splice(targetIdx, 0, movedItem);

        setPolicies(reordered);

        const updates = reordered.map((p, i) => ({
            id: p.id,
            position: i,
            tags: p.tags,
            title: p.title,
            category: p.category
        }));

        try {
            await fetch('/api/bills/update-position', {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ updates })
            });
        } catch (error) {
            console.error(error);
            fetchData();
        }
    };

    if (loading) return <div className="min-h-screen flex items-center bg-blue-50 bg-[#fcfaf7] justify-center font-serif italic text-slate-500">Retrieving Official Records from the Archive...</div>;

    const miscPolicies = policies.filter(p => !p.tags || p.tags.length === 0 || !chapters.find(c => c.name === p.tags[0]));

    return (
        <div className="min-h-screen bg-blue-50 bg-[#fcfaf7] text-slate-900 font-serif selection:bg-amber-200">
            <div className="flex mx-auto">
                   {isEligibleAdmin && !isAdmin && (
                        <button 
                            onClick={() => setIsAdmin(true)} 
                            className="mt-6 mr-10 -mb-10 px-4 py-2 mx-right ml-auto bg-slate-800 text-white font-sans text-xs font-bold uppercase rounded hover:bg-slate-700"
                        >
                            Enter Admin
                        </button>
                    )}
                    {isAdmin && (
                        <button 
                            onClick={() => setIsAdmin(false)} 
                            className="mt-6 ml-auto mr-10 -mb-10 px-4 py-2 bg-orange-200 text-orange-900 font-sans text-xs font-bold uppercase rounded hover:bg-orange-300"
                        >
                            Leave Admin
                        </button>
                    )}
            </div>
            <div className="max-w-5xl mx-auto px-8 py-20">
                
               <header className=" bg-blue-900 border-double border-slate-800 pb-10 mb-16 text-center">
                    <div className="flex justify-center mb-6 pt-6">
                        <Book size={48} className="text-slate-100" />
                    </div>
                    <h1 className="text-5xl font-black text-slate-100 tracking-tighter mb-4">Palace of the Holy Empire of America</h1>
                    <p className="text-lg uppercase tracking-[0.2em] font-sans font-semibold text-slate-200 mb-2">Official Collection of Directions</p>
                    <p className="text-sm font-sans text-slate-300">Codified Directions of the Emperor </p>
                    
                 
                </header>

                {isAdmin && (
                    <div className="mb-12 p-8 bg-red-100 border border-slate-200 shadow-sm flex gap-4 items-center">
                        <input
                            type="text"
                            placeholder="Designate New Chapter Title..."
                            value={newChapterName}
                            onChange={(e) => setNewChapterName(e.target.value)}
                            className="flex-1 p-2 border border-slate-300 font-sans outline-none focus:border-slate-800"
                        />
                        <button
                            onClick={addChapter}
                            className="bg-slate-800 text-white px-6 py-2 font-sans flex items-center gap-2 hover:bg-slate-700 transition-colors"
                        >
                            <Plus size={18} /> Establish Chapter
                        </button>
                    </div>
                )}

                <div className="space-y-3">
                    {chapters.map((chapter, idx) => {
                        const chapterPolicies = policies.filter(p => p.tags?.[0] === chapter.name);

                        return (
                            <nav
                                key={chapter.id}
                                onDragOver={handleDragOver}
                                onDrop={(e) => handleDropOnChapter(e, chapter.name)}
                                className="p-8 bg-red-100 border border-slate-200 shadow-sm"
                            >
                                 <div className={`flex items-center justify-between border-black border-b pb-2 ${!collapsed[chapter.id] && 'mb-6'} mb-0`}>
                                    <div className="flex items-center gap-3 flex-1">
                                        <button
                                            onClick={() => toggleCollapse(chapter.id)}
                                            className="flex items-center justify-center cursor-pointer hover:opacity-70 transition-opacity"
                                        >
                                            {collapsed[chapter.id] ? <ChevronRight size={18} /> : <ChevronDown size={18} />}
                                        </button>
                                        <Link 
                                            href={`/palace/chapter/${encodeURIComponent(chapter.name)}`}
                                            className="hover:opacity-80 transition-opacity flex-1"
                                        >
                                            <h2 className="font-sans font-bold uppercase text-sm tracking-widest">Campaign {idx !== 0 ? idx : "Grand"}: {chapter.name}</h2>
                                        </Link>
                                    </div>
                                    <span className="font-sans font-bold text-xs text-slate-500 uppercase">
                                        {chapterPolicies.length} Act{chapterPolicies.length > 1 && "s"}
                                    </span> 
                                </div>

                                {!collapsed[chapter.id] && (
                                    <ul className="space-y-3">
                                        {chapterPolicies.map((policy, idx) => {
                                            const globalIndex = policies.findIndex(p => p.id === policy.id) + 1;
                                            return (
                                                <li 
                                                    key={policy.id}
                                                    draggable={isAdmin}
                                                    onDragStart={(e) => handleDragStart(e, policy.id)}
                                                    onDragOver={handleDragOver}
                                                    onDrop={(e) => handleDropOnArticle(e, policy)}
                                                    className={`flex items-baseline gap-4 group ${isAdmin ? 'cursor-grab active:cursor-grabbing' : ''}`}
                                                >
                                                    {isAdmin && <GripVertical size={14} className="text-slate-400 self-center opacity-0 group-hover:opacity-100 transition-opacity" />}
                                                    <span className="font-sans font-bold text-slate-700 w-16 shrink-0">Act {idx + 1}</span>

                                                    <div className={`text-white font-sans px-2 font-bold text-[10px] py-1 rounded shrink-0 ${policy.category === 'Moral' ? 'bg-red-800' : ''}  ${policy.category === 'Introductory' ? 'bg-orange-500' : ''}  ${policy.category === 'General' ? 'bg-blue-800' : ''} ${policy.category === 'Divine' ? 'bg-purple-800' : ''} ${!['Moral', 'General', 'Divine', "Introductory"].includes(policy.category) ? 'bg-blue-900' : ''}`}>
                                                        {policy.category === 'General' ? "Human" : policy.category === "Divine" ? "Eternal" : policy.category === "Moral" ? "Natural" : policy.category}
                                                    </div> 

                                                    <Link
                                                        href={`/palace/${policy.slug}`}
                                                        className="hover:text-amber-800 hover:underline decoration-amber-500 decoration-2 transition-colors"
                                                    >
                                                        {policy.title}
                                                    </Link>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </nav>
                        );
                    })}

                    {miscPolicies.length > 0 && (
                        <nav
                            onDragOver={handleDragOver}
                            onDrop={(e) => handleDropOnChapter(e, '')}
                            className="p-8 bg-red-100 border border-slate-200 shadow-sm"
                        >
                            <div className={`flex items-center justify-between border-black border-b pb-2 ${!collapsed['misc'] && 'mb-6'} mb-0`}>
                                 <div className="flex items-center gap-3 flex-1">
                                        <button
                                            onClick={() => toggleCollapse('misc')}
                                            className="flex items-center justify-center cursor-pointer hover:opacity-70 transition-opacity"
                                        >
                                            {collapsed['misc'] ? <ChevronRight size={18} /> : <ChevronDown size={18} />}
                                        </button>
                                        <Link 
                                            href={`/palace/chapter/General`}
                                            className="hover:opacity-80 transition-opacity flex-1"
                                        >
                                            <h2 className="font-sans font-bold uppercase text-sm tracking-widest">Campaign {chapters.length + 1}: General</h2>
                                        </Link>
                                    </div>
                                <span className="font-sans font-bold text-xs text-slate-500 uppercase">
                                    {miscPolicies.length}  Act{miscPolicies.length > 1 && "s"}
                                </span>
                            </div>
                            {!collapsed['misc'] && (
                                <ul className="space-y-3 opacity-100 text-slate-900">
                                    {miscPolicies.map((policy, idx) => {
                                        const globalIndex = policies.findIndex(p => p.id === policy.id) + 1;
                                        return (
                                            <li
                                                key={policy.id}
                                                draggable={isAdmin}
                                                onDragStart={(e) => handleDragStart(e, policy.id)}
                                                onDragOver={handleDragOver}
                                                onDrop={(e) => handleDropOnArticle(e, policy)}
                                                className={`flex items-baseline gap-4 group ${isAdmin ? 'cursor-grab active:cursor-grabbing' : ''}`}
                                            >
                                                {isAdmin && <GripVertical size={14} className="text-slate-400 self-center opacity-0 group-hover:opacity-100 transition-opacity" />}
                                                <span className="font-sans font-bold text-slate-900 w-12 shrink-0">Act {idx + 1}</span>
                                                 <div className={`text-white font-sans px-2 font-bold text-[10px] py-1 rounded shrink-0 ${policy.category === 'Moral' ? 'bg-red-800' : ''} ${policy.category === 'General' ? 'bg-blue-800' : ''} ${policy.category === 'Divine' ? 'bg-purple-800' : ''} ${policy.category === 'Introductory' ? 'bg-orange-500' : ''} ${!['Moral', 'General', 'Divine'].includes(policy.category) ? 'bg-blue-900' : ''}`}>
                                                 {policy.category === 'General' ? "Human" : policy.category === "Divine" ? "Supreme" : policy.category === "Moral" ? "Natural" : policy.category}   </div>
                                              <Link
                                                    href={`/palace/${policy.slug}`}
                                                    className="hover:text-amber-800 hover:underline decoration-amber-500 decoration-2 transition-colors"
                                                   >
                                                    {policy.title}
                                                </Link>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </nav>
                    )}
                </div>
            </div>
        </div>
    );
}
